import { useCallback, useEffect, useRef, useState } from 'react'
import {
    DayView,
    WeekView,
    MonthView,
    Scheduler,
    SchedulerSlotProps,
    TimeHeaderCell,
    TimeHeaderCellProps,
} from '@progress/kendo-react-scheduler'
import Dashboard from '..'
import { useApiRequest } from 'utils/fetch-helper'
import {
    CalendarApiItem,
    CalendarSlotItem,
    StartDay,
    ViewMode,
} from 'types/calendar'
import { ServiceApiTypeEnum, ServiceItem } from 'types/service'
import { Drawer } from '@progress/kendo-react-layout'
import {
    DayDrawerItem,
    DayDrawerItemProps,
} from 'components/Calendar/DayDrawerItem'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { classNames, useWindowDimensions } from 'utils/dom-helpers'
import GViewSlot from 'components/SchedulerSlot/view-slot'
import GViewItem from 'components/CalendarItem/GViewItem'
import EmbedableModal from 'components/Modals/Calendar/Embedable'
import PrintPreviewModal from 'components/Modals/Calendar/PrintPreview'
import SchedulerSlot from 'components/SchedulerSlot'
import { getCalendarWeek } from 'utils/scheduling-helper'
import { softDeleteGroupClassAvailability } from 'services/group-class'
import { useRouter } from 'next/router'
import { useTenantContext } from 'context/TenantContext'
import { getUniqueArrayByKey } from 'utils/array-helper'
import { IntlProvider, load } from '@progress/kendo-react-intl'
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json'
import currencyData from 'cldr-core/supplemental/currencyData.json'
import weekData from 'cldr-core/supplemental/weekData.json'
import GenericSelector from 'components/DropdownSelectors/GenericSelector'

const SELECTED_MODE_KEY = 'selected_mode'
const SELECTED_LOCATION = 'selected_location'

type Mode = {
    id: string
    text: string
    translation: string
}
import { Button } from '@progress/kendo-react-buttons'
import { getTranslation } from 'utils/language-helper'
import ErrorModal from 'components/Modals/ErrorModal'
import { toYmd } from 'utils/string-localization-helper'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { DATE_REGEX } from 'types/common'
import { LocationItem } from 'types/location'
import { ChevronDownIcon } from '@heroicons/react/solid'
import FilterActionSheet from 'components/ActionSheet/FilterActionSheet'
import { useAuth } from 'context/AuthContext'
import { Popup } from '@progress/kendo-react-popup'
import LocationActionSheet from 'components/ActionSheet/LocationActionSheet'
import { DisplayTitle } from 'utils/display-title-helper'
import getConfig from 'next/config'

function TimeCell(p: TimeHeaderCellProps) {
    const { calendar_settings } = useTenantContext()
    return (
        <TimeHeaderCell
            {...p}
            format={calendar_settings.show_30_min ? 'h:mma' : 'ha'}
            className={classNames(p.className || '', 'x')}
        />
    )
}

type DateRange = {
    from: Date
    to: Date
}

function usePrevious(value: any): any {
    const ref = useRef()
    useEffect(() => {
        ref.current = value
    })
    return ref.current
}

function DashboardCalendar() {
    const { getApiRequest } = useApiRequest()
    const { setGlobalError } = useAppErrorApiContext()
    const {
        translations,
        loadComponentTranslations,
        setGlobalLoading,
        is_mobile
    } = useAppContext()
    const { PrintPreviewModal: PrintPreview } = useAuth()
    const {
        tenant,
        calendar_settings,
        locations,
        services,
        all_staff,
        force_recache_calendar,
    } = useTenantContext()
    const prev_cache = usePrevious({ locations, services })

    const router = useRouter()
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

    const { location_id, view, date: param_date } = router.query
    const [items, setItems] = useState<CalendarSlotItem[]>([])
    const [mobile_item_count, setMobileItemCount] = useState<number>(0)
    const [selected_services, setSelectedServices] = useState<
        ServiceItem[]
    >([])
    const [selected_locations, setSelectedLocations] = useState<
        LocationItem[]
    >([])
    const [single_location_index, setSingleLocationIndex] = useState<number>(0)
    const [mapped_staff, setMappedStaff] = useState<
        Record<string, string>[]
    >([])
    const [selected_staffs, setSelectedStaffs] = useState<
        Record<string, string>[]
    >([])

    let base_date = new Date()
    if (param_date && DATE_REGEX.test(param_date as string)) {
        base_date = new Date(param_date as string)
    }
    base_date.setHours(0, 0, 0, 0)
    const [active_date, setActiveDate] = useState<Date>(base_date)
    const [active_week, setActiveWeek] = useState<Date>(base_date)

    const [start, end] = getCalendarWeek(
        calendar_settings.start_day,
        base_date,
        view as ViewMode
    )
    const [dt_range, setDateRange] = useState<DateRange>({
        from: start,
        to: end,
    })
    const [is_settings_init, toggleSettingsInit] = useState<boolean>(false)

    const modes: Mode[] = [
        { id: 'staff', text: 'Staff', translation: 'mode_staff' },
        {
            id: 'location_space',
            text: 'Location Space',
            translation: 'mode_location_space',
        },
    ]
    const [selected_modes, setSelectedModes] = useState<Mode[]>(
        getValidMode()
    )
    const [show_mobile_filter, toggleMobileFilter] = useState<boolean>(false)
    const [show_mobile_location_selector, toggleMobileLocationSelector] = useState<boolean>(false)
    const [drawer_items, setDrawerItems] = useState<DayDrawerItemProps[]>(
        []
    )
    const [showWarningModal, setShowWarningModal] = useState<boolean>(false)
    const [is_expanded, expandDrawer] = useState<boolean>(false)
    const [view_mode, setViewMode] = useState<ViewMode>((view as ViewMode) || ViewMode.WEEK)
    const print_popup_ref = useRef<HTMLButtonElement | null>(null)
    const [show_print_popup, togglePrintPopup] = useState<boolean>(false)

    useEffect(() => {
        // Note: This is only used to update the query params
        //       and opted not to re-render via change of param because of the delay
        router.push({
            query: {
                ...router.query,
                view: view_mode,
                date: toYmd(active_date),
            },
        })
    }, [view_mode, active_date])

    function handleDaySelection(adjustment: number) {
        if (view_mode == ViewMode.DAY) {
            const d = new Date(
                active_date.getTime() + 3600000 * (adjustment * 24)
            )
            setActiveDate(d)
            if (dt_range.from > d || dt_range.to < d) {
                resetDateRange(d)
            }
        } else if (view_mode == ViewMode.MONTH) {
        } else if (view_mode == ViewMode.WEEK) {
        }
    }

    function handleWeekSelection(adjustment: number) {
        const d = new Date(
            active_week.getTime() + adjustment * 7 * 24 * 3600000
        )
        if (view_mode == ViewMode.WEEK) {
            setActiveDate(d)
            setActiveWeek(d)
            resetDateRange(d)
        }
    }

    function handleMonthSelection(adjustment: number) {
        if (view_mode == ViewMode.MONTH) {
            active_week.setMonth(active_week.getMonth() + adjustment)
            resetMonth(active_week)
        }
    }

    function handleDateDaySelection(adjustment: number) {
        const d = new Date(
            active_date.getTime() + 3600000 * (adjustment * 24)
        )
        setActiveDate(d)
        if (dt_range.from > d || dt_range.to < d) {
            resetDateRange(d)
        }
    }

    function handleTodayClick() {
        const today = new Date()
        if (view_mode == ViewMode.DAY) {
            setActiveDate(today)
            setActiveWeek(today)
        } else if (view_mode == ViewMode.WEEK) {
            setActiveDate(today)
            setActiveWeek(today)
            resetDateRange(today)
        } else {
            resetMonth(today)
        }
    }

    function resetDateRange(dt: Date) {
        const [start, end] = getCalendarWeek(
            calendar_settings.start_day,
            dt
        )
        setDateRange({ from: start, to: end })
    }

    function resetMonth(dt: Date) {
        const beginning = new Date(dt.getTime())
        beginning.setDate(1)
        const end = new Date(dt.getTime())
        end.setMonth(beginning.getMonth() + 1)
        end.setDate(1)
        end.setTime(end.getTime() - 24 * 3600000)
        setDateRange({
            from: getCalendarWeek(
                calendar_settings.start_day,
                beginning
            )[0],
            to: getCalendarWeek(calendar_settings.start_day, end)[1],
        })
        active_week.setMonth(beginning.getMonth())
        setActiveWeek(active_week)
        active_date.setMonth(beginning.getMonth())
        setActiveDate(active_date)
    }

    function getValidMode(): Mode[] {
        const local_storage = localStorage.getItem(SELECTED_MODE_KEY)
        const mode = modes.find((mode) => mode.id == local_storage)
        if (local_storage && mode) {
            return [mode]
        } else {
            return [modes[0]]
        }
    }

    async function getLocationServices(service_id_csv = '') {
        const from_ymd = toYmd(dt_range.from)
        const to_ymd = toYmd(dt_range.to)
        setItems([])
        if (locations?.length > 0 && services?.length > 0 && tenant?.id) {
            try {
                const appts = getApiRequest(
                    `/v1/bookings/appointments-by-tenant-id/?from=${from_ymd}&to=${to_ymd}&tenantId=${tenant?.id}`
                )
                const group_classes = locations?.map(
                    (location: LocationItem) =>
                        getApiRequest(
                            `/v1/offerings/group-class/?isRestricted=false&from=${from_ymd}&to=${to_ymd}&locationId=${location.id
                            }${service_id_csv
                                ? `&serviceIds=${service_id_csv}`
                                : ''
                            }`
                        )
                )

                const promises = Promise.all([appts, ...group_classes])

                promises.then(async (results) => {
                    const appt_classes = results
                        ?.shift()
                        ?.map(async (appt: Record<string, any>) => {
                            const svc = services?.find(
                                ({ id }) => id === appt.serviceId
                            )

                            const { attendees } = appt
                            let vals = attendees?.map(
                                async (item: string) =>
                                    getApiRequest(`/v1/clients/${item}`)
                            )
                            const res = await Promise.all(vals)
                            const allResolved = res.every(
                                (item) => !!item?.user
                            )

                            return {
                                ...svc,
                                ...appt,
                                attendees: allResolved
                                    ? res.map((item, index) => ({
                                        id: attendees[index],
                                        name: `${item?.user?.firstName} ${item?.user?.lastName}`,
                                        user: item?.user,
                                    }))
                                    : [
                                        {
                                            id: appt?.client?.user?.id,
                                            name: `${appt?.client?.user?.firstName} ${appt?.client?.user?.lastName}`,
                                        },
                                    ],
                                order: appt?.orderDto,
                                user: appt?.client?.user,
                            } as unknown as CalendarApiItem
                        })
                    const group_classes = results?.flatMap((gc) => gc)
                    const appointments = await Promise.all(appt_classes)
                    const cal_items = [...appointments, ...group_classes]

                    renderCalendarItems(
                        cal_items.filter((item: any) => !item?.deletedAt)
                    )
                })
            } catch (error) {
                setGlobalError(error)
            }
        }
    }

    function renderCalendarItems( //TODO: refactor
        xhr_group_classes: CalendarApiItem[] = []
    ) {
        if (xhr_group_classes) {
            const offerings: CalendarSlotItem[] = []
            xhr_group_classes.forEach(
                (gc: CalendarApiItem, idx: number) => {
                    const start = new Date(
                        [gc.date, gc.startTime].join('T')
                    )
                    const end = new Date(
                        start.getTime() + gc.duration * 60000
                    )
                    if (gc.deletedAt) return

                    if (gc.groupClassId)
                        offerings.push({
                            id: gc.groupClassId || `item_${idx + 1}`,
                            availability_id: gc.groupClassAvailabilityId,
                            slots: {
                                available: gc.slotsAvailable,
                                taken: gc.slotsTaken,
                                max: gc.maxCapacity,
                            },
                            service_id: gc.serviceId,
                            title: [gc.serviceName, gc.locationName].join(
                                ' @ '
                            ),
                            service_name: gc.serviceName,
                            location_name: gc.locationName,
                            location_id: gc.locationId,
                            price: gc.price,
                            setting_id: gc.groupClassSettingId,
                            space_name: gc.locationSpaceName,
                            start,
                            startTime: start,
                            end,
                            primary_hex: gc.primaryColorHex,
                            staff_id: gc.staffId,
                            staff_name: gc.staffName,
                            location_space_id: gc.locationSpaceId,
                            location_space_name: gc.locationSpaceName,
                            staff_photo: gc.staffImageUrl,
                            service_type: gc.serviceType,
                            group_class_name: gc.groupClassName,
                            online_meeting_url: gc.onlineMeetingUrl
                        })
                    else if (gc.bookingId) {
                        offerings.push({
                            id: gc.bookingId || `item_${idx + 1}`,
                            appointment_id: gc.appointmentId,
                            booking_id: gc.bookingId,
                            attendees: gc.attendees,
                            slots: {
                                available: gc.slotsAvailable,
                                taken: gc.slotsTaken,
                                max: gc.maxCapacity,
                            },
                            service_id: gc.serviceId,
                            title: [gc.serviceName, gc.locationName].join(
                                ' @ '
                            ),
                            service_name: gc.serviceName,
                            location_name: gc.locationName,
                            location_id: gc.locationId,
                            price: gc.price,
                            setting_id: gc.groupClassSettingId,
                            space_name: gc.locationSpaceName,
                            start,
                            end,
                            primary_hex: gc.primaryColorHex,
                            staff_id: gc.staffId,
                            staff_name: gc.staffName,
                            location_space_id: gc.locationSpaceId,
                            location_space_name: gc.locationSpaceName,
                            staff_photo: gc.staffImageUrl,
                            order: gc?.order,
                            user: gc?.user,
                            service_type: gc.serviceType || ServiceApiTypeEnum.APPOINTMENT,
                            group_class_name: gc.groupClassName,
                            online_meeting_url: gc.onlineMeetingUrl
                        })
                    }
                }
            )
            setItems(offerings)
        }
    }

    useEffect(() => {
        toggleSettingsInit(false)
        switch (calendar_settings.start_day) {
            case StartDay.SUNDAY:
                weekData.supplemental.weekData.firstDay.US = 'sun'
                break
            case StartDay.MONDAY:
                weekData.supplemental.weekData.firstDay.US = 'mon'
                break
        }

        if (view_mode != ViewMode.MONTH) {
            const [start, end] = getCalendarWeek(
                calendar_settings.start_day,
                active_week
            )
            if (
                start.getTime() != dt_range.from.getTime() ||
                end.getTime() != dt_range.to.getTime()
            ) {
                setDateRange({ from: start, to: end })
            }
        }

        load(likelySubtags, currencyData, weekData)
        toggleSettingsInit(true)
    }, [calendar_settings])

    useEffect(() => {
        let is_not_equal = false
        if (!prev_cache
            || prev_cache?.locations?.length != locations?.length
            || prev_cache?.services?.length != services?.length
        ) {
            is_not_equal = true
        } else {
            let index = 0
            while (index < locations?.length && !is_not_equal) {
                if (locations[index].id != prev_cache?.locations?.[index]?.id) {
                    is_not_equal = true
                } else {
                    index++
                }
            }
            if (!is_not_equal) {
                while (index < services.length && !is_not_equal) {
                    if (services[index].id != prev_cache?.services?.[index]?.id) {
                        is_not_equal = true
                    } else {
                        index++
                    }
                }
            }
        }
        if (is_not_equal) {
            getLocationServices()
        }
    }, [locations, services])

    useEffect(() => {
        if (force_recache_calendar) {
            getLocationServices()
        }
    }, [force_recache_calendar])

    useEffect(() => {
        getLocationServices()
    }, [dt_range])

    useEffect(() => {
        const local = localStorage.getItem(SELECTED_LOCATION)
        let parsed: LocationItem | undefined = undefined
        if (local) {
            parsed = JSON.parse(local)
        }
        if (
            selected_locations?.length == 1 &&
            selected_locations[0].id != parsed?.id
        ) {
            localStorage.setItem(
                SELECTED_LOCATION,
                JSON.stringify(selected_locations[0])
            )
        } else if (local) {
            if (
                parsed?.id
                && locations
                && (
                    !locations.some((location) => location.id == parsed?.id)
                    || locations.length == selected_locations.length
                )
            ) {
                localStorage.removeItem(SELECTED_LOCATION)
            }
        }
    }, [selected_locations])

    useEffect(() => {
        if (JSON.stringify(selected_services) != JSON.stringify(services)) {
            setSelectedServices(services)
        }
    }, [services])

    useEffect(() => {
        if (all_staff) {
            const map = all_staff?.map((staff) => {
                return {
                    ...staff,
                    name: `${staff.user.first_name} ${staff.user.last_name}`,
                }
            })
            setSelectedStaffs(map)
            setMappedStaff(map)
        }
    }, [all_staff])

    useEffect(() => {
        if (locations) {
            let location_storage = localStorage.getItem(SELECTED_LOCATION)
            let parsed: LocationItem | undefined = undefined
            if (!!location_storage && location_storage.length > 0) {
                parsed = JSON.parse(location_storage)
            }

            if (location_id) {
                const query = locations.find(
                    (item) => item.id === location_id
                ) as LocationItem
                setSelectedLocations([query])
            } else if (
                parsed &&
                parsed?.id &&
                locations.some((location) => location.id == parsed?.id)
            ) {
                setSelectedLocations([parsed])
            } else if (
                JSON.stringify(selected_locations) !=
                JSON.stringify(locations)
            ) {
                setSelectedLocations(locations)
            }
        }
    }, [locations, location_id, router])

    useEffect(() => {
        if (!translations.CALENDAR_TRANSLATION_ID) {
            loadComponentTranslations('CALENDAR_TRANSLATION_ID')
        }
    }, [translations])

    const getData = useCallback(() => {
        let filtered_results = items
        if (items != null && items.length > 0) {
            filtered_results = filtered_results.filter((item) =>
                selected_services.some((i) => i.id == item.service_id)
            )
            filtered_results = filtered_results.filter((item) =>
                selected_locations.some((i) => i.id === item.location_id)
            )
            filtered_results = filtered_results.filter((item) =>
                selected_staffs.some((i) => i.id === item.staff_id)
            )
        }
        return filtered_results
    }, [selected_locations, selected_services, selected_staffs, items])

    useEffect(() => {
        localStorage.setItem(SELECTED_MODE_KEY, modes[0].id)
    }, [selected_modes])

    useEffect(() => {
        if (is_mobile && locations?.length > 0) {
            setViewMode(ViewMode.DAY)
            setSelectedLocations([locations[0]])
        }
    }, [is_mobile, locations])

    const getStaffData = useCallback(() => {
        if (!selected_staffs || !all_staff) {
            return undefined as any
        }
        return selected_staffs
    }, [selected_staffs, all_staff])

    const getResources = useCallback(() => {
        if (
            selected_locations?.length == locations?.length &&
            locations?.length !== 1
        ) {
            return [
                {
                    name: 'location',
                    data: locations || [],
                    field: 'location_id',
                    valueField: 'id',
                    textField: 'name',
                },
            ]
        } else {
            if (selected_modes[0].id == modes[1].id) {
                return [
                    {
                        name: 'location_space',
                        data: selected_locations[0]?.location_spaces || [],
                        field: 'location_space_id',
                        valueField: 'id',
                        textField: 'name',
                    },
                ]
            }

            return [
                {
                    name: 'staff',
                    data: getStaffData() || [],
                    field: 'staff_id',
                    valueField: 'id',
                    textField: 'name',
                },
            ]
        }
    }, [view_mode, selected_modes, selected_locations, getStaffData()])

    const getGroup = useCallback(() => {
        if (
            !locations?.length || is_mobile ||
            (selected_modes[0].id == modes[0].id &&
                selected_staffs?.length < 1)
        ) {
            return undefined as any
        }
        return {
            resources:
                view_mode === ViewMode.DAY
                    ? locations?.length === selected_locations.length &&
                        locations?.length != 1
                        ? ['location']
                        : [selected_modes[0].id]
                    : (undefined as any),
            orientation: 'horizontal' as any,
        }
    }, [view_mode, selected_modes, selected_locations, selected_staffs])

    const WeeklyItem = useCallback((view_props: any) => {
        return (
            <GViewItem
                {...view_props}
                className="custom"
                editable={false}
                handleClick={(x) => {
                    setDrawerItems([
                        {
                            ...x,
                            service: services.find(
                                (p) => p.id == x.serviceId
                            ),
                            text: x.id,
                            onClose: () => {
                                setDrawerItems([])
                                expandDrawer(false)
                            },
                        },
                    ])
                }}
                handleDelete={async (item: CalendarSlotItem) => {
                    try {
                        setGlobalLoading(true, true)
                        const { availability_id } = item
                        if (availability_id) {
                            const res = await softDeleteGroupClassAvailability(
                                availability_id
                            )
                            if (res.ok) {
                                getLocationServices()
                            }
                            else {
                                throw res
                            }
                        }
                    } catch (error) {
                        setGlobalError(error)
                    } finally {
                        setGlobalLoading(false, true)
                    }
                }}
                onUpdate={getLocationServices}
            />
        )
    }, [active_date])

    useEffect(() => {
        if (is_mobile) {
            let found = locations?.[single_location_index]
            if (found) {
                setSelectedLocations([found])
            }
        }
    }, [single_location_index, is_mobile])

    useEffect(() => {
        if (is_mobile) {
            setMobileItemCount(getData().filter((item) => item.start.getDate() == active_date.getDate()).length)
        }
    }, [getData(), active_date])

    return (
        <Dashboard>
            <div className="flex flex-col relative w-full h-full calendar-wrap">
            
                <div className="align-middle flex flex-col sticky top-0 z-30 bg-white-0">
                <div className='page-header page-title'>{DisplayTitle()}</div>
                    
                    <div className="flex flex-col md:flex-row justify-between py-4 calendar-top items-center">
                        <div className="hidden md:flex gap-2"> 
                            <GenericSelector
                                class_name="z-10"
                                items={locations || []}
                                allow_select_all={true}
                                setSelectedItems={setSelectedLocations}
                                selected_items={selected_locations || []}
                                item_name={getTranslation(
                                    'locations',
                                    translations
                                )}
                                icon="feather-map-pin"
                            />
                            <GenericSelector
                                class_name="z-10"
                                items={services || []}
                                allow_select_all={true}
                                setSelectedItems={setSelectedServices}
                                selected_items={selected_services || []}
                                item_name={getTranslation(
                                    'services',
                                    translations
                                )}
                                is_checkbox={true}
                                icon="feather-filter"
                            />
                            <GenericSelector
                                class_name="z-10"
                                items={mapped_staff || []}
                                allow_select_all={true}
                                setSelectedItems={setSelectedStaffs}
                                selected_items={selected_staffs || []}
                                item_name={getTranslation(
                                    'staff',
                                    translations
                                )}
                                is_checkbox={true}
                                icon="feather-filter"
                            />
                            {view_mode == ViewMode.DAY &&
                                selected_locations.length == 1 && (
                                    <GenericSelector
                                        class_name="z-10 mr-3"
                                        items={modes}
                                        allow_select_all={false}
                                        setSelectedItems={setSelectedModes}
                                        selected_items={selected_modes}
                                        icon={'feather-eye'}
                                    />
                                )}
                        </div>
                        <div className="hidden md:flex">
                            <div className="calendar-btns">
                            <button
                                type="button"
                                className={classNames(
                                    view_mode == ViewMode.DAY
                                        ? 'bg-custom-dark-cyan-blue '
                                        : ' text-custom-dark-cyan-blue-0',
                                    'calendar-top-btn text-sm'
                                )}
                                onClick={() => {
                                    setViewMode(ViewMode.DAY)
                                }}
                            >
                                <Translation
                                    content_key="day"
                                    translations={translations}
                                />
                            </button>
                            <button
                                type="button"
                                className={classNames(
                                    view_mode == ViewMode.WEEK
                                        ? 'bg-custom-dark-cyan-blue '
                                        : ' text-custom-dark-cyan-blue-0',
                                    'calendar-top-btn text-sm'
                                )}
                                onClick={() => {
                                    setViewMode(ViewMode.WEEK)
                                }}
                            >
                                <Translation
                                    content_key="week"
                                    translations={translations}
                                />
                            </button>
                            <button
                                type="button"
                                className={classNames(
                                    view_mode == ViewMode.MONTH
                                        ? 'bg-custom-dark-cyan-blue text-white'
                                        : ' text-custom-dark-cyan-blue-0',
                                    'calendar-top-btn text-sm'
                                )}
                                onClick={() => {
                                    setViewMode(ViewMode.MONTH)
                                    resetMonth(active_week)
                                }}
                            >
                                <Translation
                                    content_key="month"
                                    translations={translations}
                                />
                            </button>
                            </div>
                        </div>
                        <div className="hidden md:flex gap-3 items-center">
                            <button
                                type="button"
                                className="today-btn rounded-md border p-2 px-3 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                                onClick={() => {
                                    handleTodayClick()
                                }}
                            >
                                <Translation content_key="today" />
                            </button>
                            <div className="flex items-center">
                            <div className='h-9 border border-custom-dark-cyan-blue' />
                                {/* <div
                                    className="divide-x-2 h-9 "
                                    style={{
                                        width: '1px',
                                        backgroundColor: '#BCBEC0',
                                    }}
                                ></div> */}
                            </div>
                            {/* <button
                                type="button"
                                className="rounded-lg h-12 w-12 border border-gray-300"
                                onClick={() => { }}
                            >
                                <i className="feather-code text-xl" />
                            </button>
                            <button
                                type="button"
                                className="rounded-lg h-12 w-12 border border-gray-300"
                            >
                                <i className="feather-share-2 text-xl" />
                            </button> */}
                            <button
                                type="button"
                                disabled={selected_locations.length != 1}
                                className="calendar-top-btn bg-primary-lighter bg-opacity-80 text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => {
                                    PrintPreview.setAttributes({
                                        location: selected_locations[0]
                                    })
                                    PrintPreview.open()
                                }}
                                onMouseOver={event => {
                                    if (selected_locations.length != 1) {
                                        togglePrintPopup(true)
                                    }
                                }}
                                onMouseOut={event => {
                                    if (selected_locations.length != 1) {
                                        togglePrintPopup(false)
                                    }
                                }}
                                ref={print_popup_ref}
                            >
                                <i className="feather-printer text-lg" />{' '}
                            </button>
                            <div className="flex">
                                <button
                                    type="button"
                                    className="rounded-tl-lg rounded-bl-lg p-2 bg-primary-light bg-opacity-10 text-primary"
                                    onClick={() => {
                                        handleDaySelection(-1)
                                        handleWeekSelection(-1)
                                        handleMonthSelection(-1)
                                    }}
                                >
                                    <i className="feather-chevron-left text-lg" />
                                </button>

                                <button
                                    type="button"
                                    className="p-2 bg-primary-light bg-opacity-10 text-primary border-l border-r border-white text-sm"
                                >
                                    {view_mode == ViewMode.DAY
                                        ? active_date.toLocaleDateString(
                                            'default',
                                            {
                                                dateStyle: 'medium',
                                            }
                                        )
                                        : active_week.toLocaleDateString(
                                            'default',
                                            {
                                                month: 'long',
                                                year: 'numeric',
                                            }
                                        )}{' '}
                                </button>

                                <button
                                    type="button"
                                    className="rounded-tr-lg rounded-br-lg p-2 bg-primary-light bg-opacity-10 text-primary"
                                    onClick={() => {
                                        handleDaySelection(1)
                                        handleWeekSelection(1)
                                        handleMonthSelection(1)
                                    }}
                                >
                                    <i className="feather-chevron-right text-lg" />
                                </button>
                            </div>
                        </div>
                        <div className="flex flex-row justify-center md:hidden gap-3 px-4 h-10 sm:h-12">
                            <button
                                type="button"
                                className="rounded-lg shrink-0 h-full w-10 sm:w-12 border border-gray-300"
                                onClick={() => {
                                    toggleMobileFilter(true)
                                }}
                            >
                                <i className="feather-filter text-xl" />
                            </button>
                            <div className="flex w-full text-black-one gap-x-0.5 bg-white">
                                <button
                                    type="button"
                                    className="rounded-tl-lg rounded-bl-lg h-full w-10 sm:w-12 bg-zinc-100"
                                    onClick={() => {
                                        handleDateDaySelection(-1)
                                    }}
                                >
                                    <i className="feather-chevron-left text-xl" />
                                </button>

                                <button
                                    type="button"
                                    className="w-full h-full bg-zinc-100 text-sm font-medium"
                                >
                                    {active_date.toLocaleDateString(
                                        'default',
                                        {
                                            day: '2-digit',
                                            month: 'long',
                                        }
                                    )}{' '}
                                </button>

                                <button
                                    type="button"
                                    className="rounded-tr-lg rounded-br-lg h-full w-10 sm:w-12 bg-zinc-100"
                                    onClick={() => {
                                        handleDateDaySelection(1)
                                    }}
                                >
                                    <i className="feather-chevron-right text-xl" />
                                </button>
                            </div>
                            <button
                                type="button"
                                className="rounded-lg shrink-0 h-full w-10 sm:w-12 border border-gray-300"
                            >
                                <i className="feather-share-2 text-xl" />
                            </button>
                        </div>
                    </div>
                    {is_mobile &&
                        <div className='flex flex-row shrink w-full py-2 px-4 bg-neutral-50 justify-between items-center text-center'>
                            <button
                                className='disabled:opacity-50 disabled:cursor-not-allowed'
                                disabled={locations?.length < 2 || single_location_index < 1}
                                onClick={() => {
                                    setSingleLocationIndex(single_location_index - 1)
                                }}
                            >
                                <i className='feather-chevron-left text-xl text-zinc-500' />
                            </button>
                            <button
                                className='flex flex-row items-center bg-white border border-gray-four rounded-lg py-2 px-4 disabled:cursor-not-allowed'
                                disabled={locations?.length < 2}
                                onClick={() => {
                                    toggleMobileLocationSelector(true)
                                }}
                            >
                                {locations?.length > 0
                                    ?
                                    <>
                                        <img src={locations[single_location_index]?.image_url || `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/location/location_fallback.jpg`} className="w-9 h-9 rounded-full mr-2.5" />
                                        <div className='flex grow flex-col font-medium text-left text-xs text-zinc-500'>
                                            <span className='text-sm text-dark'>
                                                {locations[single_location_index]?.name}
                                            </span>
                                            <span>
                                                {mobile_item_count} &nbsp;
                                                <Translation
                                                    content_key='calendar.appointments'
                                                />
                                            </span>

                                        </div>
                                        <ChevronDownIcon
                                            className="flex -mr-1 ml-2 h-5 w-5 text-primary-light"
                                            aria-hidden="true"
                                        />
                                    </>
                                    :
                                    <>
                                        <Translation
                                            content_key='calendar.no_locations_created'
                                        />
                                    </>
                                }

                            </button>
                            <button
                                className='disabled:opacity-50 disabled:cursor-not-allowed'
                                disabled={locations?.length < 2 || single_location_index >= locations?.length - 1}
                                onClick={() => {
                                    setSingleLocationIndex(single_location_index + 1)
                                }}
                            >
                                <i className='feather-chevron-right text-xl text-zinc-500' />
                            </button>
                        </div>
                    }
                </div>
                {is_settings_init && (
                    <IntlProvider locale={'en-US'}>
                        {view_mode != ViewMode.MONTH ? (
                            <Scheduler
                                data={getData()}
                                group={getGroup()}
                                resources={getResources()}
                                className="overflow-x-hidden aot-calendar"
                                height={is_mobile ?  `${window.innerHeight - 313}px` : "auto"}
                                view={view_mode}
                                header={() => <></>}
                                footer={() => <></>}
                                date={active_date}
                                editable={false}
                                item={WeeklyItem}
                                // viewItem={SchedulerProportionalViewItem} //auto slot height and positioning, currently bugged as of 5.8.0
                                viewSlot={(props) => {
                                    const today = new Date()
                                    const slotDate = new Date(props.start)
                                    let border_styling = ''
                                    let shouldHighlight = false
                                    if (
                                        today.getDate() ===
                                        slotDate.getDate() &&
                                        today.getMonth() ===
                                        new Date(
                                            active_week
                                        ).getMonth() &&
                                        today.getFullYear() ===
                                        slotDate.getFullYear()
                                    )
                                        shouldHighlight = true

                                    if (props.end.getMinutes() == 0) {
                                        border_styling =
                                            'border-b border-b-slate-300'
                                    } else if (
                                        props.end.getMinutes() == 30
                                    ) {
                                        border_styling = 'border-b'
                                    }
                                    return (
                                        <span
                                            className={classNames(
                                                'w-full h-6 border-r overflow-hidden box-content',
                                                border_styling,
                                                shouldHighlight &&
                                                    view_mode ===
                                                    ViewMode.WEEK
                                                    ? 'bg-sky-100'
                                                    : 'bg-white'
                                            )}
                                        >
                                            {GViewSlot(props)}
                                        </span>
                                    )
                                }}
                            >
                                <DayView
                                    showWorkHours={true}
                                    startTime={'04:00'}
                                    workDayStart={'04:00'}
                                    workDayEnd={'23:59'}
                                    editable={false}
                                    slotDuration={
                                        calendar_settings.show_30_min
                                            ? 30
                                            : 60
                                    }
                                    slotDivisions={
                                        calendar_settings.show_30_min
                                            ? 2
                                            : 4
                                    }
                                    className="overflow-y-auto"
                                />
                                <WeekView
                                    startTime={'04:00'}
                                    workDayStart={'04:00'}
                                    workDayEnd={'23:59'}
                                    editable={false}
                                    timeHeaderCell={TimeCell}
                                    showWorkHours={true}
                                    slotDuration={
                                        calendar_settings.show_30_min
                                            ? 30
                                            : 60
                                    }
                                    slotDivisions={
                                        calendar_settings.show_30_min
                                            ? 2
                                            : 4
                                    }
                                    workWeekStart={-1} // magic that allows full work week
                                    workWeekEnd={7}
                                    className="overflow-y-auto"
                                />
                            </Scheduler>
                        ) : (
                            <Scheduler
                                id="monthSchedule"
                                data={getData()}
                                height="100%"
                                date={active_date}
                                header={() => <></>}
                                footer={() => <></>}
                                slot={(p: SchedulerSlotProps) => {
                                    let shouldHighlight = false
                                    if (
                                        new Date(p.start).getDate() ===
                                        new Date().getDate()
                                    ) {
                                        shouldHighlight = true
                                    }

                                    return (
                                        <SchedulerSlot
                                            {...p}
                                            active_week={active_week}
                                            className={`g6-scheduler-slot w-full h-full`}
                                            viewDayItem={(
                                                data: unknown
                                            ) => { }}
                                            viewDay={(date: Date) => {
                                                const day_items =
                                                    items.filter(
                                                        (
                                                            i: CalendarSlotItem
                                                        ) => {
                                                            return (
                                                                i.start.getDate() ==
                                                                date.getDate()
                                                            )
                                                        }
                                                    )
                                                const d_items: DayDrawerItemProps[] =
                                                    day_items.map(
                                                        (
                                                            i: CalendarSlotItem
                                                        ) => {
                                                            return {
                                                                ...i,
                                                                text: i.title,
                                                            }
                                                        }
                                                    )
                                                let text =
                                                    date.toLocaleString(
                                                        'default',
                                                        {
                                                            month: 'long',
                                                        }
                                                    )
                                                text = `${text} ${date.getDate()}`
                                                text = `${text}, ${date.getFullYear()}`

                                                d_items.unshift({
                                                    text,
                                                    is_header: true,
                                                    count: d_items.length,
                                                    onClose: () => {
                                                        setDrawerItems([])
                                                        expandDrawer(false)
                                                    },
                                                } as DayDrawerItemProps)

                                                setDrawerItems(d_items)
                                                expandDrawer(true)
                                            }}
                                        />
                                    )
                                }}
                            >
                                <MonthView
                                    title="Month"
                                    selectedDateFormat="{0:M}"
                                    selectedShortDateFormat="{0:M}"
                                    className={`h-full overflow-y-auto`}
                                />
                            </Scheduler>
                        )}
                    </IntlProvider>
                )}

            </div>
            <Drawer
                className={classNames(
                    drawer_items.length ? 'right-0' : '-right-loose',
                    'absolute top-0'
                )}
                onOverlayClick={() => {
                    setDrawerItems([])
                    expandDrawer(false)
                }}
                expanded={is_expanded}
                position="end"
                mode="overlay"
                items={drawer_items}
                item={DayDrawerItem}
                width={487}
            />

            <PrintPreviewModal />
            <EmbedableModal />
            <ErrorModal
                error_title={getTranslation(
                    'common.access_this_page',
                    translations
                )}
                error_message={getTranslation(
                    `common.mobile_view_in_progress`,
                    translations
                )}
                onCloseModal={() => {
                    setShowWarningModal(false)
                }}
                type="warning"
                button_message="Close"
                show={showWarningModal}
            />
            <FilterActionSheet
                show={show_mobile_filter}
                toggleShow={toggleMobileFilter}
                title="calendar.filters"
                onClear={() => {
                    setSelectedLocations([])
                    setSelectedServices([])
                    setSelectedStaffs([])
                }}
            >
                <div className="flex flex-col w-full h-full items-start px-4 py-6 gap-y-5">
                    <div className='flex flex-col gap-y-2 w-full'>
                        <label className="text-sm text-dark text-left font-bold">
                            <Translation
                                content_key='location'
                            />
                            &nbsp;:
                        </label>
                        <GenericSelector
                            class_name="z-40 w-full"
                            items={locations || []}
                            allow_select_all={true}
                            setSelectedItems={setSelectedLocations}
                            selected_items={selected_locations || []}
                            item_name="locations"
                            icon="feather-map-pin text-secondary-blue"
                        />
                    </div>
                    <div className='flex flex-col gap-y-2 w-full'>
                        <label className="text-sm text-dark text-left font-bold">
                            <Translation
                                content_key='services'
                            />
                            &nbsp;:
                        </label>
                        <GenericSelector
                            class_name="z-30 w-full"
                            items={services || []}
                            allow_select_all={true}
                            setSelectedItems={setSelectedServices}
                            selected_items={selected_services || []}
                            item_name="services"
                            is_checkbox={true}
                        />
                    </div>
                    <div className='flex flex-col gap-y-2 w-full'>
                        <label className="text-sm text-dark text-left font-bold">
                            <Translation
                                content_key='staff'
                            />
                            &nbsp;:
                        </label>
                        <GenericSelector
                            class_name="z-20 w-full"
                            items={mapped_staff || []}
                            allow_select_all={true}
                            setSelectedItems={setSelectedStaffs}
                            selected_items={selected_staffs || []}
                            item_name="staff"
                            is_checkbox={true}
                        />
                    </div>
                    <button
                        className="w-full items-center justify-center bg-secondary-blue text-white rounded-lg h-10 text-sm mt-2"
                        onClick={() => {
                            toggleMobileFilter(false) //TODO: refactor
                        }}
                    >
                        <Translation content_key='calendar.apply_filters' />
                    </button>
                </div>
            </FilterActionSheet>
            <LocationActionSheet
                show={show_mobile_location_selector}
                toggleShow={toggleMobileLocationSelector}
                selected_location_index={single_location_index}
                setSelectedLocationIndex={setSingleLocationIndex}
            />
            <Popup
                anchor={print_popup_ref?.current}
                show={show_print_popup}
                animate={false}
                popupAlign={{
                    horizontal: 'center',
                    vertical: 'top',
                }}
                popupClass={
                    'bg-black text-white shadow-lg rounded-xl p-2 w-60 h-fit mt-2'
                }
                className="z-[102]"
            >
                <Translation render_as="p" content_key="calendar.print_note" />
            </Popup>
        </Dashboard>
    )
}

export default DashboardCalendar
